import { MenuItem, TextField } from '@mui/material';
import { ToggleButtonGroup, ToggleButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useState, MouseEvent } from 'react';
import classNames from 'classnames';
import numeral from 'numeral';

import IconButton from 'components/Shared/Buttons/IconButton';

import { useUserLoanDetails } from 'lib/hooks/useUserLoanDetails';
import { defaultLoanDetails } from 'redux-implementations/loanDetails';

import useEvents from 'lib/hooks/useEvents';

import InterestRateTapEvent from 'lib/Tracking/Srp/InterestRateTap';
import LoanDurationTapEvent from 'lib/Tracking/Srp/LoanDurationTap';
import DownPaymentTapEvent from 'lib/Tracking/Srp/DownPaymentTap';
import { BaseButton } from 'components/Shared/Buttons';
import { colors } from 'components/styles/variables';
import closeButton from 'public/static/images/srp/Close_Mobile.svg';
import styles, { useStyles } from './styles';
import PercentageNumberField from './PercentageNumberField';

export interface MonthlyPaymentCalculatorProps {
  onClick: () => void;
}

export default function MonthlyPaymentCalculator({
  onClick,
}: MonthlyPaymentCalculatorProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const logger = useEvents();

  const loanDurations = ['24', '36', '48', '60', '72', '84'];

  const { userLoanDetailsState, saveUserLoanDetails } = useUserLoanDetails();

  const [loanDetailsValues, setLoanDetailsValues] = useState(userLoanDetailsState);
  const [alignment, setAlignment] = useState<string>(
    userLoanDetailsState.shouldUseDownPaymentPercentage ? 'left' : 'right'
  );

  function handleAlignment(event: MouseEvent<HTMLElement>, newAlignment: string): void {
    if (newAlignment !== null) {
      setLoanDetailsValues({
        ...loanDetailsValues,
        shouldUseDownPaymentPercentage: newAlignment === 'left',
      });
      setAlignment(newAlignment);
    }
  }

  function onInterestRateClick(): Promise<void> {
    return logger.log(InterestRateTapEvent());
  }
  function onDurationClick(): Promise<void> {
    return logger.log(LoanDurationTapEvent());
  }
  function onDownPaymentTap(): Promise<void> {
    return logger.log(DownPaymentTapEvent());
  }

  return (
    <>
      <div className="monthly-payment-calculator-container">
        <div className="header">
          {t('monthlyPaymentCalculator.header')}
          <IconButton
            className="monthly-payment-calculator-close-button"
            onClick={onClick}
            aria-label={t('accessibility.closeModal')}
            style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              backgroundPosition: 'center',
              backgroundImage: `url(${closeButton})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '16px 16px',
              padding: 0,
              width: '40px',
              height: '40px',
              borderStyle: 'none',
              backgroundColor: 'transparent',
            }}
          />
        </div>
        <div className="description">{t('monthlyPaymentCalculator.description')}</div>
        <TextField
          style={{
            marginBottom: '15px',
          }}
          className={classes.inputField}
          id="interest-rate"
          label={t('monthlyPaymentCalculator.interestRate')}
          value={loanDetailsValues.interestRate}
          defaultValue={`${userLoanDetailsState.interestRate}%`}
          onClick={onInterestRateClick}
          onChange={(event): void =>
            setLoanDetailsValues({
              ...loanDetailsValues,
              interestRate: parseFloat(event.target.value),
            })
          }
          variant="standard"
          InputLabelProps={{
            className: classes.inputLabel,
            shrink: true,
            style: {
              fontSize: '16px',
              fontWeight: 'bold',
            },
          }}
          InputProps={{
            className: classes.input,
            inputComponent: PercentageNumberField as any,
            style: {
              fontSize: '20px',
            },
          }}
          placeholder={`${defaultLoanDetails.interestRate}%`}
        />
        <TextField
          style={{
            marginBottom: '15px',
          }}
          data-testid="loan-duration-field"
          className={classes.inputField}
          select
          sx={{
            fieldset: {
              display: 'none',
            },
            label: {
              left: '-14px',
            },
          }}
          id="loan-duration"
          label={t('monthlyPaymentCalculator.loanDuration')}
          value={loanDetailsValues.loanDuration}
          onClick={onDurationClick}
          onChange={(event): void =>
            setLoanDetailsValues({
              ...loanDetailsValues,
              loanDuration: parseFloat(event.target.value),
            })
          }
          InputLabelProps={{
            className: classes.inputLabel,
            style: {
              fontSize: '16px',
              fontWeight: 'bold',
            },
          }}
          InputProps={{
            className: classes.input,
            style: {
              fontSize: '20px',
            },
          }}
          SelectProps={{
            style: {
              left: '-14px',
              borderBottom: `1px solid ${colors.BLACK}`,
              borderRadius: '0px',
            },
          }}
        >
          {loanDurations.map((option) => (
            <MenuItem
              style={{ fontSize: '18px' }}
              className={classes.selectOption}
              key={option}
              value={option}
            >
              {`${option} ${t('monthlyPaymentCalculator.months')}`}
            </MenuItem>
          ))}
        </TextField>
        <div className={'down-payment-container'}>
          {loanDetailsValues.shouldUseDownPaymentPercentage && (
            <TextField
              style={{
                width: '100%',
              }}
              className={classNames(classes.downPaymentField, 'percentage')}
              id="down-payment"
              label={t('monthlyPaymentCalculator.downPayment')}
              defaultValue={`${userLoanDetailsState.downPaymentPercentage}%`}
              value={loanDetailsValues.downPaymentPercentage}
              onClick={onDownPaymentTap}
              onChange={(event): void =>
                setLoanDetailsValues({
                  ...loanDetailsValues,
                  downPaymentPercentage: parseFloat(event.target.value),
                })
              }
              variant="standard"
              InputLabelProps={{
                className: classes.inputLabel,
                shrink: true,
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold',
                },
              }}
              InputProps={{
                className: classes.input,
                inputComponent: PercentageNumberField as any,
                style: {
                  fontSize: '20px',
                },
              }}
              placeholder={`${defaultLoanDetails.downPaymentPercentage}%`}
            />
          )}
          {!loanDetailsValues.shouldUseDownPaymentPercentage && (
            <TextField
              style={{
                width: '100%',
              }}
              className={classNames(classes.downPaymentField, 'dollars')}
              id="down-payment"
              label={t('monthlyPaymentCalculator.downPayment')}
              value={numeral(loanDetailsValues.downPayment).format('$0,0')}
              defaultValue={numeral(userLoanDetailsState.downPayment).format('$0,0')}
              onClick={onDownPaymentTap}
              onChange={(event): void =>
                setLoanDetailsValues({
                  ...loanDetailsValues,
                  downPayment: numeral(event.target.value).value() as number,
                })
              }
              variant="standard"
              InputLabelProps={{
                className: classes.inputLabel,
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold',
                },
              }}
              InputProps={{ className: classes.input, style: { fontSize: '20px' } }}
              placeholder={numeral(defaultLoanDetails.downPayment).format('$0,0')}
            />
          )}
          <div className="toggle-button">
            <ToggleButtonGroup
              value={alignment}
              exclusive
              onChange={handleAlignment}
              aria-label="text alignment"
              className={classes.toggleButtonGroup}
              style={{
                height: '25px',
                paddingLeft: '10px',
              }}
            >
              <ToggleButton
                style={{
                  width: '45px',
                  fontSize: '14px',
                }}
                className={classes.toggleButton}
                value="left"
              >
                %
              </ToggleButton>
              <ToggleButton
                style={{
                  width: '45px',
                  fontSize: '14px',
                }}
                className={classes.toggleButton}
                value="right"
              >
                $
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>
        <div className="terms">{t('monthlyPaymentCalculator.terms')}</div>
        <BaseButton
          className="save-button"
          onClick={(): void => {
            saveUserLoanDetails(loanDetailsValues);
            onClick();
          }}
        >
          {t('monthlyPaymentCalculator.save')}
        </BaseButton>
      </div>
      <style jsx>{styles}</style>
    </>
  );
}
